import { AuthenticatedTemplate } from '@azure/msal-react';
import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { currentWorkflow, post_api_headers } from '../utils/Store';
import '../styles/editor.css';
import '../styles/shadows.css';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';

 const Home = () => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const [currentPage, setCurrentPage] = useAtom(currentWorkflow);
  const postapi_header = useAtom(post_api_headers)[0];

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [ADMETData, setADMETData] = useState(null);
  const [retrosynthesisData, setRetrosynthesisData] = useState(null);
  const [genChemData, setGenChemData] = useState(null);
  const [virtualScreeningData, setVirtualScreeningData] = useState(null);
  const [recentProjects, setRecentProjects] = useState(null);
  const [selectedCard, setSelectedCard] = useState(2);
  const [hoveredCard, setHoveredCard] = useState(null);
  const baseUrl = process.env.REACT_APP_API_PROJECT_MANAGEMENT_API;

  const ADMET_productId = 2;
  const retro_productId = 1;
  const genChem_productId = 3;
  const virtualScreening_productId = 4;

  // Get company ID from session storage
  const getCompanyIdFromSessionStorage = () => {
    const userProfileString = sessionStorage.getItem('userProfile');

    if (!userProfileString) {
      return null;
    }

    try {
      const userProfile = JSON.parse(userProfileString);
      if (!userProfile.companyId) {
        // You might want to handle this case specially
      }
      return userProfile.companyId;
    } catch (error) {
      return null;
    }
  };

  // Get API headers
  const getApiHeaders = () => {
    const idToken = sessionStorage.getItem('id_token');

    if (!idToken) {
      return null;
    }

    // Add logging to debug headers
    const headers = {
      Authorization: `Bearer ${idToken}`,
      ...postapi_header,
    };

    return headers;
  };

   const fetchData = async (productId, setData) => {
    const companyId = getCompanyIdFromSessionStorage();
    const headers = getApiHeaders();

    if (!companyId || !headers) {
      return;
    }

    try {
      const url = `${baseUrl}/api/projects/get-project-status?productId=${encodeURIComponent(productId)}&companyId=${encodeURIComponent(companyId)}`;

      const response = await fetch(url, { headers });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch data for product ${productId}. Status: ${response.status}. Response: ${errorText}`,
        );
      }

      const data = await response.json();
      setData(data);
    } catch (error) {}
  };

  const fetchRecentProjects = async () => {
    const companyId = getCompanyIdFromSessionStorage();
    const headers = getApiHeaders();

    if (!companyId || !headers) {
      return;
    }

    try {
      const url = `${baseUrl}/api/projects/get-recent-projects?companyId=${encodeURIComponent(companyId)}`;

      const response = await fetch(url, { headers });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch recent projects. Status: ${response.status}. Response: ${errorText}`,
        );
      }

      const data = await response.json();
      setRecentProjects(data);
    } catch (error) {}
  };

 
  const fetchAllData = async () => {
    try {
      // Check if we have what we need
      const companyId = getCompanyIdFromSessionStorage();
      const headers = getApiHeaders();

      if (!companyId || !headers) {
        setLoading(false);
        return;
      }

      await Promise.all([
        fetchData(ADMET_productId, setADMETData),
        fetchData(retro_productId, setRetrosynthesisData),
        fetchData(genChem_productId, setGenChemData),
        fetchData(virtualScreening_productId, setVirtualScreeningData),
        fetchRecentProjects(),
      ]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  let timeoutId = null;
  let isMounted = true;

  const checkProfileAndFetchData = () => {
    const userProfileString = sessionStorage.getItem('userProfile');
    const idToken = sessionStorage.getItem('id_token');

    if (userProfileString && idToken) {
      fetchAllData(isMounted);        // ← pass the flag
    } else {
      timeoutId = setTimeout(checkProfileAndFetchData, 1000);
    }
  };

  checkProfileAndFetchData();
  return () => {
    isMounted = false;
    clearTimeout(timeoutId);
  };
}, []);


  const handleCardClick = (productId) => {
    setSelectedCard(productId);
    const routes = {
      [ADMET_productId]: '/admet',
      [retro_productId]: '/retrosynthesis',
      [genChem_productId]: '/generativeai',
      [virtualScreening_productId]: '/virtualscreening',
    };
    navigate(routes[productId] || '/');
  };

  const renderWorkflowCard = (title, data, productId) => (
    <div
      className="px-4 sm:w-full lg:w-[278px] md:w-[247px] rounded-lg cursor-pointer transition-colors duration-300 bg-gray-300 dashboard-background hover:bg-lime-600"
      onClick={() => handleCardClick(productId)}
      onMouseEnter={() => setHoveredCard(productId)}
      onMouseLeave={() => setHoveredCard(null)}
    >
      <h2
        className={`py-2 text-md font-semibold text-left ${hoveredCard === productId ? 'text-white' : 'text-[#000000]'}`}
      >
        {title}
      </h2>
      {data && (
        <div className="mt-6">
          <div className="flex justify-between items-center">
            <span
              className={`text-sm text-[#000000] font-semibold ${hoveredCard === productId ? 'text-white' : 'text-[#000000]'}`}
            >
              No. Of Projects
            </span>
            <span
              className={`text-lg font-bold text-[#000000] ${hoveredCard === productId ? 'text-white' : 'text-[#000000]'}`}
            >
              {data.totalNoOfProjects}
            </span>
          </div>
          <div className="flex justify-between items-center mt-5 mb-7">
            {[
              { label: 'In-Progress', key: 'inprogressProjects' },
              { label: 'Completed', key: 'completedProjects' },
              { label: 'Pending', key: 'pendingProjects' },
            ].map((status, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <span
                  className={`text-xs ${hoveredCard === productId ? 'text-white' : 'text-[#000000]'}`}
                >
                  {status.label}
                </span>
                <span className="rounded-full shadow-lg font-bold mt-2 bg-white w-10 h-10 flex items-center justify-center">
                  {data[status.key] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="px-4 md:px-8 lg:px-16">
      <div className="text-2xl mt-4">
        <span className="opacity-75">Welcome to Centella AI!</span>
        <span className="ml-2 text-3xl font-medium">{activeAccount?.name}</span>
      </div>
      <AuthenticatedTemplate>
        {activeAccount && (
          <div className="pt-12">
            {loading ? (
              <div className="flex justify-center items-center h-[60vh]">
                <Loader />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ml-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:col-span-2">
                  {renderWorkflowCard('ADMET', ADMETData, ADMET_productId)}
                  {renderWorkflowCard(
                    'Generative AI',
                    genChemData,
                    genChem_productId,
                  )}
                  {renderWorkflowCard(
                    'Retrosynthesis',
                    retrosynthesisData,
                    retro_productId,
                  )}
                  {renderWorkflowCard(
                    'Virtual Screening',
                    virtualScreeningData,
                    virtualScreening_productId,
                  )}
                </div>
                <div className="w-full bg-white shadow-md rounded-md">
                  <div className="bg-gray-700 rounded-t-md text-white p-3">
                    Recent Project History
                  </div>
                  <div className="p-4 overflow-y-scroll scrollbar scrollbar-thin scrollbar-thumb-[#A0AEC0] scrollbar-track-[#F4F4F4] space-y-4 h-[370px]">
                    {recentProjects?.map((project, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between">
                          <span className="font-semibold">Project Name:</span>
                          <span>{project.projectName}</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between">
                          <span className="font-semibold">Workflow:</span>
                          <span>{project.productName}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </AuthenticatedTemplate>
    </div>
  );
};

export default Home;


